export const isObject = (value: unknown): boolean => {
  const type = typeof value;
  return value !== undefined && value != null && !Array.isArray(value) && (type === 'object' || type === 'function');
};

export const isEmpty = (value: object): boolean => Object.keys(value).length === 0;

export const isArrayOfObjects = (arr: unknown[]): boolean => arr.every(element => isObject(element));

const emptyToNull = (value: unknown): unknown => (value === undefined || value === '' ? null : value);

/**
 * Converts empty strings and undefined values to null, at the **top level only**.
 *
 * Not a forms helper. A form root sends its schema's output as it is — every
 * owned field a value or `null`, `nullable()` in the schema being where
 * `'' → null` lives (ADR 0001). The one caller left is the conversation
 * update (`conversationToBack`, and the `userToBack` it maps participants
 * through), which builds its body from a front entity, not from a form. Do
 * not put it back on a payload path.
 *
 * `null` is the backend's "delete this field" marker, and it only has that
 * meaning at the root of the payload — see the `_.pickBy(fields, _.isNull)` pass
 * in the members controller. One level down, a null is simply stored as-is.
 *
 * This function used to recurse. That is what broke entity addresses: clearing
 * an optional sub-field (`addressCompl`, `region`, …) wrote `null` into the
 * stored address, and on the next read `addressSchema` rejected it — the whole
 * address then failed to parse and was silently dropped from every subsequent
 * save. The address became permanently uneditable while the UI still reported
 * success. Nested values are now passed through untouched; a sub-field is
 * cleared by omitting it from the object (the backend replaces the subdocument
 * wholesale), not by nulling it.
 *
 * @param obj - The object to process
 * @returns A shallow copy with top-level empty strings and undefined turned into null
 *
 * @example
 * ```ts
 * const payload = { name: '', category: undefined, address: { city: '', zip: '12345' } };
 * const result = emptyStringToNull(payload);
 * // Result: { name: null, category: null, address: { city: '', zip: '12345' } }
 * ```
 */
export function emptyStringToNull<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return emptyToNull(obj) as T;
  }

  const result = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = emptyToNull(obj[key]) as T[Extract<keyof T, string>];
    }
  }
  return result;
}
